import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, StatusBadge } from "@/components/common/Primitives";
import { KpiCard } from "@/components/common/KpiCard";
import { fmtEUR } from "@/lib/format";
import { RENEWAL_STAGE_MAP } from "@/lib/constants";
import { TrendingUp, Target, Trophy, XCircle } from "lucide-react";

export default function Sales() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["renewals-sales"], queryFn: async () => (await api.get("/renewals")).data });
  const stages = data?.stages || {};
  const count = (s) => (stages[s] || []).length;
  const won = count("verlaengert");
  const lost = count("verloren");
  const open = count("anstehend") + count("kontaktversuch") + count("angebot_gesendet") + count("nachfassen");
  const rate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;
  const funnel = ["anstehend", "kontaktversuch", "kontakt_erfolgreich", "angebot_gesendet", "nachfassen", "verlaengert"];
  const maxC = Math.max(1, ...funnel.map((s) => count(s)));

  return (
    <div>
      <PageHeader title="Vertriebs-Pipeline" description="Konversion und offene Vertriebschancen" />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard testId="sales-kpi-open" label="Offene Chancen" value={open} icon={TrendingUp} tone="info" onClick={() => navigate("/verlaengerungen")} />
        <KpiCard testId="sales-kpi-won" label="Gewonnen" value={won} icon={Trophy} />
        <KpiCard testId="sales-kpi-lost" label="Verloren" value={lost} icon={XCircle} tone="danger" />
        <KpiCard testId="sales-kpi-rate" label="Gewinnquote" value={`${rate}%`} icon={Target} tone="accent" />
      </div>

      <SectionCard title="Trichter">
        <div className="space-y-2">
          {funnel.map((s) => (
            <div key={s} className="flex items-center gap-3">
              <span className="w-40 shrink-0 text-xs text-muted-foreground">{RENEWAL_STAGE_MAP[s].label}</span>
              <div className="h-7 flex-1 overflow-hidden rounded-md bg-muted/40">
                <div className="flex h-full items-center rounded-md bg-accent/80 px-2 text-xs font-semibold text-accent-foreground transition-all"
                  style={{ width: `${Math.max(6, (count(s) / maxC) * 100)}%` }}>{count(s)}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Offene Angebote" className="mt-4">
        <div className="space-y-2">
          {(stages.angebot_gesendet || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Keine offenen Angebote.</p>}
          {(stages.angebot_gesendet || []).map((c) => (
            <div key={c.id} onClick={() => navigate(`/vertraege/${c.id}`)} className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border px-4 py-2.5 hover:bg-muted/40">
              <div className="min-w-0"><div className="truncate text-sm font-medium">{c.customer_name}</div><div className="text-xs text-muted-foreground">{c.provider} · {fmtEUR(c.monthly_fee)}/M</div></div>
              <StatusBadge status={c.renewal_status} map={RENEWAL_STAGE_MAP} />
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
